import { useState, useRef, useCallback } from 'react';

export const useTrackMapCore = (tabId, getAuthToken, currentVehicle, currentDateRange, setCurrentVehicle, formatDate) => {
  // Состояния
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [mapInitialized, setMapInitialized] = useState(false);
  const [trackData, setTrackData] = useState([]);
  
  // Рефы
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const routePathRef = useRef(null);
  const animatedMarkerRef = useRef(null);
  const highlightedPathRef = useRef(null);

  // Инициализация карты (точная копия из TrackMap)
  const initializeMap = useCallback(() => {
    console.log('TrackReport: Инициализация карты...');
    
    // Проверяем наличие контейнера карты
    if (!mapContainerRef.current) {
      console.error('TrackReport: Контейнер карты не найден при инициализации');
      return;
    }
    
    try {
      // Определяем ID для контейнера карты
      const mapElementId = `map-${tabId || 'default'}`;
      
      // Если карта уже инициализирована, уничтожаем её для повторной инициализации
      if (mapInstanceRef.current) {
        console.log('TrackReport: Уничтожаем существующую карту перед повторной инициализацией');
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      
      // Устанавливаем небольшую задержку перед инициализацией для предотвращения ошибок DOM
      setTimeout(() => {
        try {
          // Проверяем доступность Leaflet
          if (!L) {
            console.error('TrackReport: Библиотека Leaflet не загружена');
            return;
          }
          
          // Инициализируем карту Leaflet с обработкой ошибок (точная копия из TrackMap)
          const map = L.map(mapElementId, {
            center: [43.238949, 76.889709], // Центр Алматы по умолчанию
            zoom: 13,
            layers: [
                L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                    attribution: '',
                    maxZoom: 20
                })
            ],
            // Предотвращаем зависание анимации при скрытых вкладках
            fadeAnimation: false,
            zoomAnimation: false
          });
          
          // Сохраняем экземпляр карты в ref
          mapInstanceRef.current = map;
          
          // Добавляем обработчик события изменения размера с защитой от ошибок
          map.on('resize', () => {
            try {
              console.log('TrackReport: Изменение размера карты');
              if (map && map._container && document.body.contains(map._container)) {
                map.invalidateSize();
              }
            } catch (error) {
              console.error('TrackReport: Ошибка при изменении размера карты:', error);
            }
          });
          
          // Защита от ошибок при масштабировании
          map.on('zoom', () => {
            if (map && !map._animatingZoom && map._mapPane && map._mapPane.offsetWidth === 0) {
              // Если карта невидима или размер равен 0, сбрасываем анимацию
              map._resetView(map.getCenter(), map.getZoom(), true);
            }
          });
          
          // Устанавливаем флаги инициализации
          setMapInitialized(true);
          setIsLoading(false);
          
          console.log('TrackReport: Карта успешно инициализирована');
          
          // После инициализации карты, загружаем данные трека если есть ТС и даты (точная копия из TrackMap)
          if (currentVehicle && currentVehicle.id && currentDateRange.startDate && currentDateRange.endDate) {
            console.log('TrackReport: Запуск загрузки данных трека после инициализации карты:', {
              vehicle: currentVehicle.name,
              startDate: formatDate(currentDateRange.startDate),
              endDate: formatDate(currentDateRange.endDate)
            });
            setTimeout(() => fetchTrackData(currentVehicle, currentDateRange), 300);
          } else {
            console.log('TrackReport: Не загружаем данные трека: не выбран транспорт или даты');
            
            // Пытаемся получить данные из localStorage, если не был предоставлен currentVehicle
            if (!currentVehicle || !currentVehicle.id) {
              try {
                const savedVehicle = localStorage.getItem('lastSelectedVehicle');
                if (savedVehicle) {
                  const parsedVehicle = JSON.parse(savedVehicle);
                  if (parsedVehicle && parsedVehicle.id) {
                    console.log('TrackReport: Загружаем транспорт из localStorage:', parsedVehicle);
                    
                    // Добавляем источник данных
                    parsedVehicle._source = 'localStorage_init';
                    parsedVehicle._updateTimestamp = new Date().getTime();
                    
                    setCurrentVehicle(parsedVehicle);
                    
                    // Если есть даты, загрузим данные трека
                    if (currentDateRange.startDate && currentDateRange.endDate) {
                      setTimeout(() => fetchTrackData(parsedVehicle, currentDateRange), 350);
                    }
                  }
                }
              } catch (error) {
                console.warn('TrackReport: Ошибка при получении транспорта из localStorage:', error);
              }
            }
          }
          
        } catch (mapInitError) {
          console.error('TrackReport: Ошибка при инициализации карты после задержки:', mapInitError);
          setIsLoading(false);
        }
      }, 150); // Задержка для гарантии что DOM полностью отрисован
    } catch (error) {
      console.error('TrackReport: Ошибка при инициализации карты:', error);
      setIsLoading(false);
    }
  }, [tabId, currentVehicle, currentDateRange, setCurrentVehicle, formatDate]);

  // Загрузка данных трека (точная копия из TrackMap)
  const fetchTrackData = useCallback(async (vehicleToLoad, dateRangeToLoad) => {
    try {
      if (!vehicleToLoad || !vehicleToLoad.imei) {
        console.warn('TrackReport: Невозможно загрузить данные трека: не выбрано транспортное средство');
        return;
      }
      
      const startIsValid = dateRangeToLoad.startDate instanceof Date && 
                          !isNaN(dateRangeToLoad.startDate.getTime());
      const endIsValid = dateRangeToLoad.endDate instanceof Date && 
                        !isNaN(dateRangeToLoad.endDate.getTime());
      
      if (!startIsValid || !endIsValid) {
        console.warn('TrackReport: Невозможно загрузить данные трека: невалидные даты:', {
          startDate: dateRangeToLoad.startDate,
          endDate: dateRangeToLoad.endDate
        });
        return;
      }
      
      setIsLoading(true);
      setLoadingProgress(5);
      setLoadingStatus('Подготовка запроса...');
      
      try {
        console.log('TrackReport: Загрузка данных трека для:', {
          imei: vehicleToLoad.imei,
          name: vehicleToLoad.name,
          startDate: dateRangeToLoad.startDate.toISOString(),
          endDate: dateRangeToLoad.endDate.toISOString()
        });
        
        const startISOString = dateRangeToLoad.startDate.toISOString();
        const endISOString = dateRangeToLoad.endDate.toISOString();
        const startDateString = dateRangeToLoad.startDate.toISOString().split('T')[0];
        const endDateString = dateRangeToLoad.endDate.toISOString().split('T')[0];
        
        setLoadingProgress(15);
        setLoadingStatus('Подготовка API запроса...');
        
        const primaryApiUrl = `/api/telemetry/v3/${vehicleToLoad.imei}/track?startTime=${encodeURIComponent(startISOString)}&endTime=${encodeURIComponent(endISOString)}`;
        const fallbackApiUrl = `/api/v1/tracks/${vehicleToLoad.imei}?from=${startDateString}&to=${endDateString}`;
        
        const authToken = getAuthToken();
        
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
        
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
        
        setLoadingProgress(25);
        setLoadingStatus('Соединение с сервером...');
        
        let response;
        let data;
        let useBackupApi = false;
        
        try {
          response = await fetch(primaryApiUrl, {
            method: 'GET',
            headers: headers
          });
          
          setLoadingProgress(40);
          setLoadingStatus('Получение данных...');
          
          if (!response.ok) {
            console.warn(`TrackReport: Основной API вернул ошибку: ${response.status}. Пробуем запасной URL.`);
            setLoadingStatus('Подключение к запасному API...');
            useBackupApi = true;
          } else {
            data = await response.json();
            setLoadingProgress(60);
            setLoadingStatus('Обработка данных...');
          }
        } catch (primaryError) {
          console.warn(`TrackReport: Ошибка при запросе к основному API: ${primaryError.message}. Пробуем запасной URL.`);
          setLoadingStatus('Подключение к запасному API...');
          useBackupApi = true;
        }
        
        if (useBackupApi) {
          try {
            setLoadingProgress(45);
            response = await fetch(fallbackApiUrl, {
              method: 'GET',
              headers: headers
            });
            
            setLoadingProgress(55);
            
            if (!response.ok) {
              throw new Error(`TrackReport: Запасной API вернул ошибку: ${response.status} ${response.statusText}`);
            }
            
            data = await response.json();
            setLoadingProgress(65);
            setLoadingStatus('Обработка данных...');
          } catch (fallbackError) {
            throw new Error(`TrackReport: Ошибка при запросе к запасному API: ${fallbackError.message}`);
          }
        }
        
        let trackPoints = [];
        
        setLoadingProgress(70);
        setLoadingStatus('Преобразование данных...');
        
        if (data) {
          if (Array.isArray(data.points)) {
            console.log(`TrackReport: Успешно получены данные трека: ${data.points.length} точек`);
            
            trackPoints = data.points.map((point, index) => ({
              id: index,
              latitude: point.lat || point.latitude,
              longitude: point.lng || point.lon || point.longitude,
              timestamp: new Date(point.timestamp || point.time || point.datetime),
              speed: point.speed || 0,
              altitude: point.altitude || point.alt || 0,
              course: point.course || point.heading || point.direction || 0,
              satellites: point.satellites || point.sats || 0,
              fuel: point.fuel || point.fuelLevel || 0
            }));
          } else if (Array.isArray(data)) {
            console.log(`TrackReport: Успешно получены данные трека: ${data.length} точек`);
            
            trackPoints = data.map((point, index) => ({
              id: index,
              latitude: point.lat || point.latitude,
              longitude: point.lng || point.lon || point.longitude,
              timestamp: new Date(point.timestamp || point.time || point.datetime),
              speed: point.speed || 0,
              altitude: point.altitude || point.alt || 0,
              course: point.course || point.heading || point.direction || 0,
              satellites: point.satellites || point.sats || 0,
              fuel: point.fuel || point.fuelLevel || 0
            }));
          } else if (data.tracks && Array.isArray(data.tracks)) {
            console.log(`TrackReport: Успешно получены данные трека: ${data.tracks.length} точек`);
            
            trackPoints = data.tracks.map((point, index) => ({
              id: index,
              latitude: point.lat || point.latitude,
              longitude: point.lng || point.lon || point.longitude,
              timestamp: new Date(point.timestamp || point.time || point.datetime),
              speed: point.speed || 0,
              altitude: point.altitude || point.alt || 0,
              course: point.course || point.heading || point.direction || 0,
              satellites: point.satellites || point.sats || 0,
              fuel: point.fuel || point.fuelLevel || 0
            }));
          } else if (data.data && Array.isArray(data.data)) {
            console.log(`TrackReport: Успешно получены данные трека: ${data.data.length} точек`);
            
            trackPoints = data.data.map((point, index) => ({
              id: index,
              latitude: point.lat || point.latitude,
              longitude: point.lng || point.lon || point.longitude,
              timestamp: new Date(point.timestamp || point.time || point.datetime),
              speed: point.speed || 0,
              altitude: point.altitude || point.alt || 0,
              course: point.course || point.heading || point.direction || 0,
              satellites: point.satellites || point.sats || 0,
              fuel: point.fuel || point.fuelLevel || 0
            }));
          } else if (data.error) {
            throw new Error(`TrackReport: Ошибка API: ${data.error}`);
          } else {
            throw new Error('TrackReport: Неизвестный формат данных от API');
          }
        } else {
          throw new Error('TrackReport: API не вернул данные');
        }
        
        setLoadingProgress(90);
        setLoadingStatus('Финализация...');
        
        setTrackData(trackPoints);
        setLoadingProgress(100);
        setLoadingStatus('Данные загружены');
        
        console.log('TrackReport: Данные трека успешно обработаны:', trackPoints.length, 'точек');
        
      } catch (error) {
        console.error('TrackReport: Ошибка при загрузке данных трека:', error);
        setTrackData([]);
      } finally {
        setTimeout(() => {
          setIsLoading(false);
          setLoadingProgress(0);
          setLoadingStatus('');
        }, 500);
      }
    } catch (error) {
      console.error('TrackReport: Критическая ошибка при загрузке трека:', error);
      setIsLoading(false);
    }
  }, [getAuthToken]);

  return {
    // Состояния
    isLoading,
    loadingProgress,
    loadingStatus,
    mapInitialized,
    trackData,
    setTrackData,
    
    // Рефы
    mapContainerRef,
    mapInstanceRef,
    markersRef,
    routePathRef,
    animatedMarkerRef,
    highlightedPathRef,
    
    // Функции
    initializeMap,
    fetchTrackData
  };
}; 